import React from 'react';
import {
  Checkbox, FormControlLabel, Button, Stack, Typography, Chip, Tooltip,
  CircularProgress, Box
} from '@mui/material';
import * as ETVConstants from '../ETVConstants';
import { CheckCircle, Block } from '@mui/icons-material';
import ETVTable from '../tools/ETVTable';

class CardAccessMgmt extends React.Component {
  constructor(props) {
    super(props);
  }

  state = {
    floors: [],
    floorsLoaded: false,
    accessMap: {}, // floor_id → true/false (access granted)
    loadingBulk: false,
    error: null,
  }

  componentDidMount() {
    this.loadAllFloors();
    this.loadAccessFloors();
  }

  componentDidUpdate() {
    if (!this.state.floorsLoaded) {
      this.loadAllFloors();
    }
  }

  // ---------- Data loading ----------
  loadAllFloors() {
    const accessToken = localStorage.getItem("etv.access_token");
    const reqUserID = localStorage.getItem("etv.user_id");
    const url = ETVConstants.getServerBase()+"/floor_access?action=get_floors&location_id="+this.props.location_id;
    fetch(url,{headers: { 'user_id': reqUserID, 'access_token': accessToken }})
      .then(res => res.json())
      .then(
        (result) => {
          this.setState(prev => {
            const next = { ...prev.accessMap };
            (result.list || []).forEach(f => {
              if (!f.public_access && next[f.floor_id] === undefined) {
                next[f.floor_id] = false;
              }
            });
            return { floors: result.list || [], floorsLoaded: true, accessMap: next, error: null };
          });
        },
        (error) => {
          this.setState({ floors: [], floorsLoaded: true, error });
        }
      );
  }

  loadAccessFloors() {
    //console.log("loading access floors");
    const accessToken = localStorage.getItem("etv.access_token");
    const reqUserID = localStorage.getItem("etv.user_id");
    let url = ETVConstants.getServerBase()+"/floor_access?action=get_access_no_validity&location_id="+this.props.location_id;
    url += "&card_id="+encodeURIComponent(this.props.card_id);
    //console.log("Request URL:", url);
    fetch(url, {
      headers: { 'user_id': reqUserID, 'access_token': accessToken }
    })
    .then(async (res) => {
      const text = await res.text();
      //console.log("HTTP Status:", res.status);
      //console.log("Raw Response:", text);
      let json = null;
      try {
        json = JSON.parse(text);
        //console.log("Parsed JSON:", json);
      } catch (err) {
        //console.error("Response is not valid JSON");
        return { error: "invalid_json", raw: text };
      }
      return json;
    })
    .then(
      (result) => {
        if (!result || result.error) return;

        const list = result.list || [];
        //console.log("Accessible floor list:", list);

        this.setState(prev => {
          const map = { ...prev.accessMap };
          list.forEach(item => { map[item.floor_id] = true; });
          return { accessMap: map };
        });
      },
      (error) => {
        console.error("loadAccessFloors failed:", error);
        this.setState({ error });
      }
    );
  }

  // ---------- Server update ----------
  updateAccessFloor(floorID, cardID, access) {
    //console.log("updateAccessFloor");
    const accessToken = localStorage.getItem("etv.access_token");
    const reqUserID = localStorage.getItem("etv.user_id");
    let url = ETVConstants.getServerBase()+"/floor_access?action=update_floor_access";
    url += "&floor_id="+floorID;
    url += "&card_id="+encodeURIComponent(cardID);
    url += "&access="+access;
    //console.log("Request URL:", url);
    return fetch(url, {headers: { 'user_id': reqUserID, 'access_token': accessToken }}).then(async (res) => {
      const text = await res.text();  // read raw body
      //console.log("HTTP Status:", res.status);
      //console.log("Raw Response:", text);
      try {
        const json = JSON.parse(text);
        //console.log("Parsed JSON:", json);
        if (!res.ok || !json || json.result !== true) {
          throw new Error((json && (json.error || json.data)) || 'Failed to update floor access');
        }
        return json;
      } catch (err) {
        console.error("Response is not valid JSON");
        throw err;
      }
    });
  }

  // ---------- Helpers for bulk/select-all ----------
  getPrivateFloorIds() {
    return (this.state.floors || []).filter(f => !f.public_access).map(f => f.floor_id);
  }

  countSelected(ids) {
    let c = 0;
    ids.forEach(id => { if (this.state.accessMap[id]) c++; });
    return c;
  }

  getMasterCheckboxState() {
    const ids = this.getPrivateFloorIds();
    if (ids.length === 0) return { checked: false, indeterminate: false };
    const selected = this.countSelected(ids);
    if (selected === 0) return { checked: false, indeterminate: false };
    if (selected === ids.length) return { checked: true, indeterminate: false };
    return { checked: false, indeterminate: true };
  }

  async toggleAllPrivate(toValue) {
    const ids = this.getPrivateFloorIds();
    if (ids.length === 0) return;

    // Optimistic UI
    this.setState(prev => {
      const next = { ...prev.accessMap };
      ids.forEach(id => { next[id] = toValue; });
      return { accessMap: next, loadingBulk: true };
    });

    try {
      const results = await Promise.all(
        ids.map(id => this.updateAccessFloor(id, this.props.card_id, toValue))
      );
      //console.log("Bulk toggle results:", results);
    } catch (e) {
      console.error("Bulk toggle failed", e);
    } finally {
      this.setState({ loadingBulk: false });
    }

  }

  // ---------- Render ----------
  render() {
    const privateIds = this.getPrivateFloorIds();
    const selectedPrivate = this.countSelected(privateIds);
    const floorColumns = [
      {
        title: 'Floor',
        field: 'floor_label',
        render: row => <Typography variant="body2" sx={{fontWeight:600}}>{String(row.floor_label || '')}</Typography>,
      },
      {
        title: 'Public access',
        field: 'public_access',
        sorting: false,
        render: row => row.public_access
          ? <Chip size="small" icon={<CheckCircle fontSize="small"/>} label="Public" color="success"/>
          : <Chip size="small" icon={<Block fontSize="small"/>} label="Private" color="error"/>,
      },
      {
        title: 'Card access',
        sorting: false,
        searchable: false,
        render: row => {
          const isPublic = !!row.public_access;
          const granted = !!this.state.accessMap[row.floor_id];
          if (isPublic) return <Chip size="small" variant="outlined" color="success" label="Always"/>;
          return <FormControlLabel
            control={<Checkbox checked={granted} onChange={async (event) => {
              const next = event.target.checked;
              this.setState(prev => ({accessMap:{...prev.accessMap,[row.floor_id]:next}}));
              try {
                await this.updateAccessFloor(row.floor_id,this.props.card_id,next);
              } catch (error) {
                this.setState(prev => ({accessMap:{...prev.accessMap,[row.floor_id]:!next}}));
                console.error('Update floor access failed',error);
              }
            }}/>} label={granted?'Granted':'Not granted'}/>;
        },
      },
    ];

    return (
      <Box>

          {/* Toolbar */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'flex-start', sm: 'center' }} sx={{ mb: 1.5 }}>

            <Tooltip title={ETVConstants.trans('select_all_hint') || 'Affects non-public floors'}>
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => this.toggleAllPrivate(true)}
                  disabled={this.state.loadingBulk || privateIds.length === 0 || selectedPrivate === privateIds.length}
                >
                  {ETVConstants.trans('select_all') || 'Select all'}
                </Button>
              </span>
            </Tooltip>

            <Tooltip title={ETVConstants.trans('deselect_all_hint') || 'Affects non-public floors'}>
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => this.toggleAllPrivate(false)}
                  disabled={this.state.loadingBulk || privateIds.length === 0 || selectedPrivate === 0}
                >
                  {ETVConstants.trans('deselect_all') || 'Deselect all'}
                </Button>
              </span>
            </Tooltip>

            <Typography variant="body2" sx={{ ml: 'auto', opacity: 0.8 }}>
              {ETVConstants.trans('selected') || 'Selected'}: {selectedPrivate} / {privateIds.length}
            </Typography>
          </Stack>

          {/* Table */}
          {!this.state.floorsLoaded ? (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 4, justifyContent: 'center' }}>
              <CircularProgress size={22} />
              <Typography variant="body2">{ETVConstants.trans('loading') || 'Loading floors...'}</Typography>
            </Stack>
          ) : (
            <ETVTable title="" data={this.state.floors || []} columns={floorColumns} search={false} pageSize={10}/>
          )}
      </Box>
    );
  }
}

export default CardAccessMgmt;
